import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

import styles from "./Menu.module.scss";

interface MenuProps {
  trigger: (isOpen: boolean) => React.ReactNode;
  children: React.ReactNode | ((closeMenu: () => void) => React.ReactNode);
  align?: "left" | "right";
}

const Menu: React.FC<MenuProps> = ({ trigger, children, align = "right" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  
  const closeMenu = () => setIsOpen(false)
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuHeight = menuRef.current?.scrollHeight || 0;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement = spaceBelow < menuHeight && spaceAbove >= menuHeight ? "top" : "bottom";
      const top = placement === "top"
        ? rect.top - menuHeight - 8
        : rect.bottom + 8;

      setPosition((prevPosition) => {
        if (prevPosition.top === top && prevPosition.left === (align === "right" ? rect.right - 220 : rect.left)) {
          return prevPosition;
        }
        return {
          top,
          left: align === "right" ? rect.right - 220 : rect.left,
        };
      });
    }
  }, [align]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  return (
    <div className={styles.container} ref={triggerRef}>
      <div className={styles.trigger} role="button" tabIndex={0} onClick={() => setIsOpen((prev) => !prev)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen((prev) => !prev); } }}>
        {trigger(isOpen)}
      </div>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className={`${styles.dropdown} ${isOpen ? styles.open : styles.close}`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          {typeof children === "function" ? children(closeMenu) : children}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Menu;
